import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Fonction de compression d'image
const compressImageFile = async (
  file: File,
  options: {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
    maxSizeMB?: number;
  } = {}
): Promise<File> => {
  const {
    maxWidth = 1920,
    maxHeight = 1920,
    quality = 0.85,
    maxSizeMB = 2,
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        // Redimensionner si nécessaire
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Impossible de créer le contexte canvas'));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        // Fonction récursive pour réduire la qualité si nécessaire
        const attemptCompression = (currentQuality: number) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Échec de la compression'));
                return;
              }

              const maxSizeBytes = maxSizeMB * 1024 * 1024;
              if (blob.size <= maxSizeBytes || currentQuality <= 0.5) {
                const compressedFile = new File([blob], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                // Réduire la qualité et réessayer
                attemptCompression(currentQuality - 0.05);
              }
            },
            file.type,
            currentQuality
          );
        };

        attemptCompression(quality);
      };
      img.onerror = () => reject(new Error('Échec du chargement de l\'image'));
    };
    reader.onerror = () => reject(new Error('Échec de la lecture du fichier'));
  });
};

export interface SiteImage {
  id: string;
  image_key: string;
  image_url: string;
  alt_text_fr?: string;
  alt_text_en?: string;
  alt_text_pt?: string;
  category: string;
  position: number;
  is_active: boolean;
  notes?: string;
}

export const useSiteImages = () => {
  const [images, setImages] = useState<Record<string, SiteImage>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('site_images')
        .select('*')
        .eq('is_active', true);

      if (error) throw error;

      // Convert array to object with image_key as key
      const imagesMap = (data || []).reduce((acc, img) => {
        acc[img.image_key] = img;
        return acc;
      }, {} as Record<string, SiteImage>);

      setImages(imagesMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const updateImage = async (imageKey: string, file: File) => {
    try {
      // === LOGS DE DÉBOGAGE ===
      console.log('🔍 [useSiteImages] Fichier sélectionné:', {
        name: file.name,
        type: file.type,
        size: file.size,
        extension: file.name.split('.').pop()?.toLowerCase()
      });

      // Validation 1: Extension du fichier (PRIORITÉ ABSOLUE)
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const allowedFormats = ['jpg', 'jpeg', 'png', 'webp'];
      const blockedExtensions = ['heic', 'heif', 'tiff', 'tif', 'bmp', 'svg', 'gif', 'avif'];
      
      console.log('🔍 Extension détectée:', fileExt);
      
      // Bloquer HEIC en priorité
      if (fileExt && blockedExtensions.includes(fileExt)) {
        console.log('❌ Extension bloquée:', fileExt);
        throw new Error(`Les fichiers .${fileExt.toUpperCase()} ne sont pas supportés. Convertissez en JPG, PNG ou WEBP.`);
      }
      
      if (!fileExt || !allowedFormats.includes(fileExt)) {
        console.log('❌ Extension non autorisée:', fileExt);
        throw new Error(`Format .${fileExt?.toUpperCase() || 'inconnu'} non supporté. Utilisez JPG, PNG ou WEBP uniquement.`);
      }
      
      // Validation 2: Type MIME (secondaire)
      console.log('🔍 Type MIME:', file.type);
      const blockedTypes = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
      
      if (!file.type || file.type === '' || file.type === 'application/octet-stream') {
        console.log('⚠️ Type MIME vide ou générique détecté');
        // Extension déjà vérifiée, c'est OK
      } else if (blockedTypes.includes(file.type.toLowerCase())) {
        console.log('❌ Type MIME bloqué:', file.type);
        throw new Error('Format HEIC (iPhone) non supporté. Convertissez en JPG avant upload.');
      } else if (file.type && !file.type.match(/^image\/(jpeg|png|webp)$/)) {
        console.log('❌ Type MIME non autorisé:', file.type);
        throw new Error('Le fichier doit être une image JPG, PNG ou WEBP valide.');
      }

      // Validation 3: Taille originale (avant compression)
      const maxOriginalSize = 50 * 1024 * 1024; // 50MB max avant compression
      if (file.size > maxOriginalSize) {
        throw new Error(`Fichier trop volumineux: ${(file.size / 1024 / 1024).toFixed(1)} MB. Maximum 50 MB avant compression.`);
      }
      
      // Compression de l'image
      const compressedFile = await compressImageFile(file, {
        maxWidth: 1920,
        maxHeight: 1920,
        quality: 0.85,
        maxSizeMB: 2
      });
      
      // Convention de nommage organisée
      const category = 'pages';
      const identifier = imageKey.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const fileName = `${category}-${identifier}.${fileExt}`;
      const filePath = `${category}/${fileName}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('site-assets')
        .upload(filePath, compressedFile, {
          contentType: file.type,
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('site-assets')
        .getPublicUrl(filePath);

      // Update or insert image record
      const { error: upsertError } = await supabase
        .from('site_images')
        .upsert({
          image_key: imageKey,
          image_url: publicUrl,
          is_active: true
        }, {
          onConflict: 'image_key'
        });

      if (upsertError) throw upsertError;

      // Refresh images
      await fetchImages();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Erreur lors de la mise à jour');
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const deleteImage = async (imageKey: string) => {
    try {
      const { error } = await supabase
        .from('site_images')
        .delete()
        .eq('image_key', imageKey);

      if (error) throw error;

      // Refresh images
      await fetchImages();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Erreur lors de la suppression');
    }
  };

  return { 
    images, 
    loading, 
    error, 
    refetch: fetchImages,
    updateImage,
    deleteImage 
  };
};